import { fetchRecommendProducts } from '@/api/product';
import { useState, useCallback } from 'react';
import { Product } from '@/components/Product/ProductCard';
import { priceInfo } from '@/utils/price';
import { useReporting } from '@/hooks/useReporting';
import { useStore } from '@/providers/app-store-provider';
import { getProductDisplayLabel } from '@/utils/goods-list-utils';
import { ProviderType } from '@/reporting';
import { setSession } from '@/utils/tools';

export const useTrackLikeList = () => {
  const userInfo = useStore((state) => state.userInfo);
  const currency = useStore((state) => state.currency);
  const { trackEvent } = useReporting();

  const [likeListState, setLikeListState] = useState({
    list: [],
  });

  const fetchLikeList = async () => {
    fetchRecommendProducts({
      group_id: 10,
    }).then((res) => setLikeListState({ list: (res.data as any).track_order_list }));
  };

  /**
   * 推荐商品埋点方法
   */
  const sendRecommendTrack = useCallback(
    (
      index: number,
      product: Product & {
        business_sku: string;
        sku: string;
        spu_review_count: string;
        wish_num: string;
      },
      sdEventName: string,
    ) => {
      const sdEventParams = {
        product_index: index,
        business_sku: product.business_sku,
        shop_sku: product.shop_sku,
        product_sku: product.sku,
        product_id: `${product.id}`,
        product_name: product.short_name,
        product_price: parseFloat(priceInfo(userInfo, product, currency).price),
        current_product_price: parseFloat(priceInfo(userInfo, product, currency).sell_price),
        base_current_product_price: parseFloat(priceInfo(userInfo, product, 'USD').sell_price),
        dollar_exchange_rate: parseFloat(currency.exchange_rate),
        currency: currency.name,
        product_display_label: getProductDisplayLabel(product as any, 1),
        product_review_num: parseInt(product.spu_review_count, 10),
        product_rating: parseFloat(product.spu_review_stars),
        product_collect_num: parseInt(product.wish_num, 10),
      };
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    },
    [userInfo, currency, trackEvent],
  );

  /**
   * 推荐商品点击事件
   */
  const handleRecommendMktClick = useCallback(
    (product: any, listData: Product[], index?: number) => {
      setSession('zl_goodsDetailEnterPageScene', '订单追踪页猜你喜欢商品坑位');
      // 如果没有传递index，则查找index（兼容旧版本）
      const actualIndex =
        index !== undefined
          ? index
          : listData.findIndex((item) => item.shop_sku === product.shop_sku);
      sendRecommendTrack(actualIndex, product, 'TrackOrderPageMkt_Click');
    },
    [sendRecommendTrack],
  );

  /**
   * 推荐商品曝光事件
   */
  const handleRecommendMktExp = useCallback(
    (product: any, listData: Product[], index?: number) => {
      // 如果没有传递index，则查找index（兼容旧版本）
      const actualIndex =
        index !== undefined
          ? index
          : listData.findIndex((item) => item.shop_sku === product.shop_sku);
      sendRecommendTrack(actualIndex, product, 'TrackOrderPageMkt_Exp');
    },
    [sendRecommendTrack],
  );

  /**
   * 推荐商品有效曝光事件
   */
  const handleRecommendMktValidExp = useCallback(
    (product: any, listData: Product[], index?: number) => {
      // 如果没有传递index，则查找index（兼容旧版本）
      const actualIndex =
        index !== undefined
          ? index
          : listData.findIndex((item) => item.shop_sku === product.shop_sku);
      sendRecommendTrack(actualIndex, product, 'TrackOrderPageValidMkt_Exp');
    },
    [sendRecommendTrack],
  );

  return {
    likeListState,
    fetchLikeList,
    handleRecommendMktExp,
    handleRecommendMktValidExp,
    handleRecommendMktClick,
  };
};
